import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Briefcase,
  MapPin,
  Clock,
  Building,
  CheckCircle,
  Circle,
  GraduationCap,
  LogOut,
  Loader2,
} from 'lucide-react';
import api from '../lib/api';
import { removeToken } from '../lib/auth';

export default function RoleDetailPage() {
  const { roleId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [role, setRole] = useState(null);
  const [acknowledged, setAcknowledged] = useState({
    about_role: false,
    key_responsibilities: false,
    required_skills: false,
    preferred_skills: false,
    experience: false,
    education: false,
  });

  useEffect(() => {
    const fetchRole = async () => {
      try {
        const response = await api.get(`/api/roles/${roleId}`);
        setRole(response.data);
      } catch (error) {
        toast.error('Failed to load role details');
        navigate('/roles');
      } finally {
        setLoading(false);
      }
    };
    fetchRole();
  }, [roleId, navigate]);

  const handleLogout = () => {
    removeToken();
    toast.success('Logged out successfully');
    navigate('/');
  };

  const allAcknowledged = Object.values(acknowledged).every((val) => val === true);

  const handleApply = () => {
    if (!allAcknowledged) {
      toast.error('Please acknowledge all sections before applying');
      return;
    }
    navigate(`/apply/${roleId}`, { state: { eligibility: acknowledged } });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white/40 flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-teal-500 animate-spin" />
      </div>
    );
  }

  if (!role) return null;

  return (
    <div className="min-h-screen bg-white/40">
      {/* Header */}
      <header className="bg-white border-b border-white/40 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-2">
              <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-teal-600 rounded-xl flex items-center justify-center">
                <span className="text-white font-bold text-xl">G</span>
              </div>
              <div>
                <h1 className="text-xl font-bold text-navy-900">girgit</h1>
                <p className="text-xs text-neutral-600">Role Details</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <Link
                to="/roles"
                className="text-neutral-700 hover:text-navy-900 font-medium transition-colors"
              >
                Browse Roles
              </Link>
              <Link
                to="/dashboard"
                className="text-neutral-700 hover:text-navy-900 font-medium transition-colors"
              >
                My Applications
              </Link>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 text-neutral-700 hover:text-navy-900 transition-colors"
                data-testid="logout-button"
              >
                <LogOut className="w-5 h-5" />
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
        {/* Back Link */}
        <Link
          to="/roles"
          className="inline-flex items-center text-neutral-600 hover:text-navy-900 mb-6 transition-colors"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back to Roles
        </Link>

        {/* Role Header */}
        <div className="bg-white rounded-xl shadow-soft border border-white/40 p-8 mb-6">
          <h1 className="text-4xl font-bold text-navy-900 mb-4">{role.title}</h1>
          <div className="flex flex-wrap gap-4 text-neutral-700">
            <div className="flex items-center">
              <Building className="w-5 h-5 mr-2 text-teal-600" />
              {role.department}
            </div>
            <div className="flex items-center">
              <MapPin className="w-5 h-5 mr-2 text-teal-600" />
              {role.location}
            </div>
            <div className="flex items-center">
              <Clock className="w-5 h-5 mr-2 text-teal-600" />
              {role.employment_type}
            </div>
            <div className="flex items-center">
              <Briefcase className="w-5 h-5 mr-2 text-teal-600" />
              {role.experience_min}-{role.experience_max} years
            </div>
          </div>
        </div>

        {/* Eligibility Sections */}
        <div className="space-y-6 mb-8">
          {/* About the Role */}
          <div className="bg-white rounded-xl shadow-soft border border-white/40 p-6" data-testid="section-about-role">
            <div className="flex items-start justify-between mb-4">
              <h2 className="text-2xl font-semibold text-navy-800">About the Role</h2>
              {acknowledged.about_role ? (
                <CheckCircle className="w-6 h-6 text-emerald-600" />
              ) : (
                <Circle className="w-6 h-6 text-neutral-300" />
              )}
            </div>
            <p className="text-neutral-700 leading-relaxed mb-6 whitespace-pre-wrap">
              {role.about_role}
            </p>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={acknowledged.about_role}
                onChange={(e) =>
                  setAcknowledged({ ...acknowledged, about_role: e.target.checked })
                }
                className="w-5 h-5 text-teal-600 border-white/40 rounded focus:ring-2 focus:ring-teal-500"
                data-testid="acknowledge-about-role"
              />
              <span className="text-navy-700 font-medium">
                I am eligible for this role based on the description
              </span>
            </label>
          </div>

          {/* Key Responsibilities */}
          <div className="bg-white rounded-xl shadow-soft border border-white/40 p-6" data-testid="section-responsibilities">
            <div className="flex items-start justify-between mb-4">
              <h2 className="text-2xl font-semibold text-navy-800">
                Key Responsibilities
              </h2>
              {acknowledged.key_responsibilities ? (
                <CheckCircle className="w-6 h-6 text-emerald-600" />
              ) : (
                <Circle className="w-6 h-6 text-neutral-300" />
              )}
            </div>
            <ul className="space-y-2 mb-6">
              {role.key_responsibilities.map((item, index) => (
                <li key={index} className="flex items-start gap-3 text-neutral-700">
                  <span className="text-teal-600 mt-1">•</span>
                  {item}
                </li>
              ))}
            </ul>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={acknowledged.key_responsibilities}
                onChange={(e) =>
                  setAcknowledged({ ...acknowledged, key_responsibilities: e.target.checked })
                }
                className="w-5 h-5 text-teal-600 border-white/40 rounded focus:ring-2 focus:ring-teal-500"
                data-testid="acknowledge-responsibilities"
              />
              <span className="text-navy-700 font-medium">
                I am capable of handling these responsibilities
              </span>
            </label>
          </div>

          {/* Required Skills */}
          <div className="bg-white rounded-xl shadow-soft border border-white/40 p-6" data-testid="section-required-skills">
            <div className="flex items-start justify-between mb-4">
              <h2 className="text-2xl font-semibold text-navy-800">
                Required Skills
              </h2>
              {acknowledged.required_skills ? (
                <CheckCircle className="w-6 h-6 text-emerald-600" />
              ) : (
                <Circle className="w-6 h-6 text-neutral-300" />
              )}
            </div>
            <div className="flex flex-wrap gap-2 mb-6">
              {role.required_skills.map((skill, index) => (
                <span
                  key={index}
                  className="px-3 py-1 bg-teal-100 text-teal-800 rounded-full text-sm font-medium"
                >
                  {skill}
                </span>
              ))}
            </div>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={acknowledged.required_skills}
                onChange={(e) =>
                  setAcknowledged({ ...acknowledged, required_skills: e.target.checked })
                }
                className="w-5 h-5 text-teal-600 border-white/40 rounded focus:ring-2 focus:ring-teal-500"
                data-testid="acknowledge-required-skills"
              />
              <span className="text-navy-700 font-medium">
                I possess all the required skills
              </span>
            </label>
          </div>

          {/* Preferred Skills */}
          <div className="bg-white rounded-xl shadow-soft border border-white/40 p-6" data-testid="section-preferred-skills">
            <div className="flex items-start justify-between mb-4">
              <h2 className="text-2xl font-semibold text-navy-800">
                Preferred Skills
              </h2>
              {acknowledged.preferred_skills ? (
                <CheckCircle className="w-6 h-6 text-emerald-600" />
              ) : (
                <Circle className="w-6 h-6 text-neutral-300" />
              )}
            </div>
            <div className="flex flex-wrap gap-2 mb-6">
              {role.preferred_skills.map((skill, index) => (
                <span
                  key={index}
                  className="px-3 py-1 bg-neutral-100 text-neutral-700 rounded-full text-sm font-medium"
                >
                  {skill}
                </span>
              ))}
            </div>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={acknowledged.preferred_skills}
                onChange={(e) =>
                  setAcknowledged({ ...acknowledged, preferred_skills: e.target.checked })
                }
                className="w-5 h-5 text-teal-600 border-white/40 rounded focus:ring-2 focus:ring-teal-500"
                data-testid="acknowledge-preferred-skills"
              />
              <span className="text-navy-700 font-medium">
                I have understanding of the preferred skills
              </span>
            </label>
          </div>

          {/* Experience */}
          <div className="bg-white rounded-xl shadow-soft border border-white/40 p-6" data-testid="section-experience">
            <div className="flex items-start justify-between mb-4">
              <h2 className="text-2xl font-semibold text-navy-800">
                Experience Required
              </h2>
              {acknowledged.experience ? (
                <CheckCircle className="w-6 h-6 text-emerald-600" />
              ) : (
                <Circle className="w-6 h-6 text-neutral-300" />
              )}
            </div>
            <p className="text-neutral-700 mb-6">
              <strong>{role.experience_min}-{role.experience_max} years</strong> of relevant
              professional experience
            </p>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={acknowledged.experience}
                onChange={(e) =>
                  setAcknowledged({ ...acknowledged, experience: e.target.checked })
                }
                className="w-5 h-5 text-teal-600 border-white/40 rounded focus:ring-2 focus:ring-teal-500"
                data-testid="acknowledge-experience"
              />
              <span className="text-navy-700 font-medium">
                I meet the experience requirements
              </span>
            </label>
          </div>

          {/* Education */}
          <div className="bg-white rounded-xl shadow-soft border border-white/40 p-6" data-testid="section-education">
            <div className="flex items-start justify-between mb-4">
              <h2 className="text-2xl font-semibold text-navy-800">
                Education Requirements
              </h2>
              {acknowledged.education ? (
                <CheckCircle className="w-6 h-6 text-emerald-600" />
              ) : (
                <Circle className="w-6 h-6 text-neutral-300" />
              )}
            </div>
            <ul className="space-y-2 mb-6">
              {role.education.map((item, index) => (
                <li key={index} className="flex items-start gap-3 text-neutral-700">
                  <GraduationCap className="w-5 h-5 text-teal-600 mt-0.5" />
                  {item}
                </li>
              ))}
            </ul>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={acknowledged.education}
                onChange={(e) =>
                  setAcknowledged({ ...acknowledged, education: e.target.checked })
                }
                className="w-5 h-5 text-teal-600 border-white/40 rounded focus:ring-2 focus:ring-teal-500"
                data-testid="acknowledge-education"
              />
              <span className="text-navy-700 font-medium">
                I meet the education requirements
              </span>
            </label>
          </div>
        </div>

        {/* Apply Button - Sticky at bottom */}
        <div className="sticky bottom-0 bg-white border-t border-white/40 py-4">
          <div className="flex justify-between items-center">
            <div className="text-sm text-neutral-600">
              {allAcknowledged ? (
                <span className="text-emerald-600 font-medium">
                  ✓ All sections acknowledged
                </span>
              ) : (
                <span>
                  Please acknowledge all sections to proceed ({Object.values(acknowledged).filter(v => v).length}/6)
                </span>
              )}
            </div>
            <button
              onClick={handleApply}
              disabled={!allAcknowledged}
              className="px-8 py-3 bg-teal-500 text-white rounded-xl hover:bg-teal-600 transition-all duration-200 shadow-soft hover:shadow-soft font-medium disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-soft"
              data-testid="apply-button"
            >
              Apply for this Role
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}
